import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { AccountListRowContent, ACCOUNT_LIST_ROW_CLASS } from '@/components/account/AccountListRow';
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectItem,
  SelectPortal,
  SelectScrollView,
  SelectTrigger,
} from '@/components/ui/select';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const SELF_VALUE = 'self';

type ActAsAccountRowProps = {
  /** Names the control for a screen reader. The visible label is "Act as" followed by the current choice. */
  title: string;
  subtitle: string;
  /** The account being acted as, or undefined for the signed-in user. */
  actAsUserId: string | undefined;
  /** What the row shows as the current choice. */
  currentLabel: string;
  accounts: { id: string; label: string }[];
  /** Called with the chosen account's id, or undefined for Myself. */
  onSelect: (userId: string | undefined) => void;
};

/**
 * The staff-only act-as picker, drawn as one Settings-style row that opens the
 * account list. The Gluestack select's trigger is a plain pressable that takes any
 * children, so the row is the trigger itself. On native the list is the select's
 * bottom sheet. On web the select overlays an invisible native `<select>` across the
 * whole trigger, so a click anywhere on the row opens the browser's own picker,
 * which is also what a phone browser wants. Resetting the trigger's border and
 * height turns the boxed dropdown into a row, and the layout comes from the same
 * content component the other rows use. Choosing calls `onSelect` with the account
 * id, or undefined for Myself.
 */
export function ActAsAccountRow(props: ActAsAccountRowProps) {
  const { title, subtitle, actAsUserId, currentLabel, accounts, onSelect } = props;

  // On web the focused element is the invisible native select over the row, whose own outline
  // would be invisible too, and the select does not report focus-visible. So the row draws the
  // app's standard ring itself, and only for keyboard focus, never after a mouse click.
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);

  const detachFocusListeners = useRef<(() => void) | null>(null);

  // A callback ref, so the listeners attach whenever the trigger's element mounts. The select's own
  // typing names the pressable's props rather than its element, hence the loose parameter.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attachFocusListeners = useCallback((node: any) => {
    detachFocusListeners.current?.();
    detachFocusListeners.current = null;

    // The invisible native select is the trigger's sibling inside the select's root, not its
    // child, so its focus events bubble to the root and never reach the trigger.
    const root: HTMLElement | null | undefined = node?.parentElement;

    if (!isWeb || !root?.addEventListener) {
      return;
    }

    const handleFocusIn = (event: Event) =>
      setHasKeyboardFocus((event.target as HTMLElement).matches?.(':focus-visible') ?? false);
    const handleFocusOut = () => setHasKeyboardFocus(false);

    root.addEventListener('focusin', handleFocusIn);
    root.addEventListener('focusout', handleFocusOut);

    detachFocusListeners.current = () => {
      root.removeEventListener('focusin', handleFocusIn);
      root.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return (
    <Select
      selectedValue={actAsUserId ?? SELF_VALUE}
      onValueChange={(value) => onSelect(value === SELF_VALUE ? undefined : value)}
      aria-label={title}>
      <SelectTrigger
        className={twJoin(
          ACCOUNT_LIST_ROW_CLASS,
          'h-auto rounded-none border-0 data-[focus=true]:border-0 data-[focus=true]:web:shadow-none data-[focus=true]:data-[hover=true]:web:shadow-none data-[hover=true]:border-0',
          hasKeyboardFocus && 'ring-2 ring-indicator-info'
        )}
        ref={attachFocusListeners}
        {...getAccessibilityProps({
          role: 'button',
          label: `${title}, currently ${currentLabel}`,
          hint: 'Opens a list of accounts',
        })}>
        <AccountListRowContent
          title={`Act as ${currentLabel}`}
          subtitle={subtitle}
          trailing="chevron-down"
        />
      </SelectTrigger>

      <SelectPortal>
        <SelectBackdrop />
        <SelectContent className="max-h-[70vh] w-full pb-8">
          <SelectDragIndicatorWrapper>
            <SelectDragIndicator />
          </SelectDragIndicatorWrapper>
          <SelectScrollView>
            <SelectItem label="Myself" value={SELF_VALUE} />
            {accounts.map((account, index) => (
              <SelectItem key={`${account.id}-${index}`} label={account.label} value={account.id} />
            ))}
          </SelectScrollView>
        </SelectContent>
      </SelectPortal>
    </Select>
  );
}
